import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";

export const SUPPORTED_ENTITIES = [
  "categories",
  "attributes",
  "attributeOptions",
  "products",
  "productVariants",
  "productAttributes",
  "ingredients",
  "variantIngredients",
  "optionIngredients",
  "categoryIngredients",
  "customers",
  "promotions",
  "users",
] as const;

export type EntityName = (typeof SUPPORTED_ENTITIES)[number];

export const ENTITY_META: Record<
  EntityName,
  { label: string; group: "catalog" | "ops" | "people" }
> = {
  categories: { label: "Categories", group: "catalog" },
  attributes: { label: "Attributes", group: "catalog" },
  attributeOptions: { label: "Attribute options", group: "catalog" },
  products: { label: "Products", group: "catalog" },
  productVariants: { label: "Product variants", group: "catalog" },
  productAttributes: { label: "Product ↔ attribute links", group: "catalog" },
  ingredients: { label: "Ingredients", group: "ops" },
  variantIngredients: { label: "Variant recipes", group: "ops" },
  optionIngredients: { label: "Option recipes", group: "ops" },
  categoryIngredients: { label: "Category recipes", group: "ops" },
  customers: { label: "Customers", group: "people" },
  promotions: { label: "Promotions", group: "ops" },
  users: { label: "Users (passwords excluded)", group: "people" },
};

export interface DataBundle {
  version: "1";
  exportedAt: string;
  entities: Partial<Record<EntityName, Record<string, unknown>[]>>;
}

export interface ImportSummary {
  entity: EntityName;
  inserted: number;
  updated: number;
  failed: number;
  errors: string[];
}

export interface ImportResult {
  summaries: ImportSummary[];
  totalRows: number;
}

export async function fetchExport(
  entities: EntityName[] | null,
): Promise<DataBundle> {
  const params = entities && entities.length > 0
    ? { entities: entities.join(",") }
    : undefined;

  return apiClient.get<DataBundle>("/data/export", params);
}

export function useImportData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bundle: DataBundle) =>
      apiClient.post<ImportResult>("/data/import", bundle),
    onSuccess: () => {
      queryClient.clear();
    },
  });
}
